import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './ChatBox.css';
import { getAdminAssistantApiUrl } from '../utils/apiUrls';
import { optionalAuthHeaders } from '../utils/billingApi';

const BACKEND_URL = getAdminAssistantApiUrl();

const HOME_SUGGESTIONS = [
    { label: 'When is my next closing?', value: 'When is my next closing?' },
    { label: 'What docs are missing?', value: 'What documents are missing?' },
    { label: 'Summarize my transactions', value: 'Summarize my open transactions' },
];

const DEAL_SUGGESTIONS = [
    { label: "When is this deal's closing?", value: "When is this deal's closing?" },
    { label: 'What docs are missing on this deal?', value: 'What documents are missing on this deal?' },
    { label: 'Summarize this transaction', value: 'Summarize this transaction' },
];

const ChatBox = ({
    userEmail,
    userName,
    getAccessTokenSilently,
    getIdTokenClaims,
    entityHint,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [chatId, setChatId] = useState(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const suggestions = entityHint ? DEAL_SUGGESTIONS : HOME_SUGGESTIONS;

    useEffect(() => {
        if (!isExpanded || !messagesContainerRef.current) return;
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }, [messages, isExpanded]);

    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage = inputValue.trim();
        setInputValue('');
        setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
        setIsLoading(true);

        try {
            const headers = await optionalAuthHeaders({
                getAccessTokenSilently,
                getIdTokenClaims,
            });
            const body = {
                user_email: userEmail,
                user_name: userName,
                message: userMessage,
            };
            if (entityHint) {
                body.entity_hint = entityHint;
            }
            if (chatId) {
                body.chat_id = chatId;
            }

            const response = await axios.post(`${BACKEND_URL}/chat`, body, { headers });
            if (response.data.chat_id) {
                setChatId(response.data.chat_id);
            }

            const aiResponse = response.data.response || 'I apologize, but I could not process your request.';
            setMessages(prev => [...prev, {
                type: 'assistant',
                text: aiResponse,
                contracts: response.data.referenced_contracts || [],
            }]);
        } catch (error) {
            console.error('Chat error:', error);
            const quotaText = error.response?.status === 429
                ? String(error.response.data?.response || '').trim()
                : '';
            setMessages(prev => [...prev, {
                type: 'assistant',
                text: quotaText || 'I apologize, but I encountered an error. Please try again.',
                isError: true,
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div
            className={`chat-box ${isExpanded ? 'expanded' : 'collapsed'}`}
            data-testid="ask-keyflo"
            data-entity-hint={entityHint || ''}
        >
            <div className="chat-header" onClick={toggleExpanded}>
                <div className="chat-header-content">
                    <span className="chat-icon">💬</span>
                    <span className="chat-title">Ask KeyFlo</span>
                    {!isExpanded && (
                        <span className="chat-hint">
                            {entityHint
                                ? 'Click to ask about this deal'
                                : 'Click to ask about your transactions'}
                        </span>
                    )}
                </div>
                <button className="chat-toggle" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                    {isExpanded ? '−' : '+'}
                </button>
            </div>

            {isExpanded && (
                <div className="chat-body">
                    <div className="chat-messages" ref={messagesContainerRef}>
                        {messages.length === 0 && (
                            <div className="chat-welcome">
                                <p>👋 Hi! I'm KeyFlo, your AI Transaction Coordinator.</p>
                                <p>
                                    {entityHint
                                        ? 'Ask me about this deal’s dates, documents, or status.'
                                        : 'Ask me about your upcoming deadlines, documents, or transaction status.'}
                                </p>
                                <div className="chat-suggestions">
                                    {suggestions.map((item) => (
                                        <button
                                            key={item.value}
                                            type="button"
                                            onClick={() => setInputValue(item.value)}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {messages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.type} ${msg.isError ? 'error' : ''}`}>
                                {msg.type === 'assistant' && <span className="message-avatar">🤖</span>}
                                <div className="message-content">{msg.text}</div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="chat-message assistant loading">
                                <span className="message-avatar">🤖</span>
                                <div className="message-content">
                                    <span className="typing-indicator">
                                        <span></span><span></span><span></span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="chat-input-container">
                        <input
                            ref={inputRef}
                            type="text"
                            className="chat-input"
                            placeholder={
                                entityHint
                                    ? 'Ask about this deal’s dates, documents, or status...'
                                    : 'Ask about dates, documents, or status...'
                            }
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                        />
                        <button
                            className="chat-send"
                            type="button"
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim() || isLoading}
                        >
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBox;
